import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';

const Sidebar = () => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  
  const navItems = [
    { icon: 'fa-terminal', path: '/rcon-hacking', label: 'RCON Hack' },
    { icon: 'fa-user-shield', path: '/account-checker', label: 'Accounts' },
    { icon: 'fa-network-wired', path: '/ddos', label: 'DDoS Test' },
    { icon: 'fa-server', path: '/servers', label: 'Servers' },
    { icon: 'fa-database', path: '/server-data-extraction', label: 'Extract' },
    { icon: 'fa-robot', path: '/bots', label: 'Bots' },
    { icon: 'fa-mask', path: '/secret', label: 'Secret' },
  ];

  useEffect(() => {
    const currentItem = navItems.find(item => item.path === location.pathname);
    if (currentItem) {
      setActiveItem(currentItem);
    }
  }, [location.pathname]);

  return (
    <div className="w-20 bg-dark-600 h-full flex flex-col items-center py-6 border-r border-dark-400 relative sidebar-container">
      {/* Logo at top - links to home */}
      <Link to="/" className="mb-10">
        <motion.div 
          className="transition-all duration-300 transform hover:scale-110"
          whileHover={{ scale: 1.1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <div className="w-12 h-12 bg-gradient-to-br from-accent-pink to-accent-purple rounded-xl flex items-center justify-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-purple to-accent-pink opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <i className="fas fa-cube text-white text-xl relative z-10"></i>
            <div className="absolute inset-0 bg-dark-800 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
          </div>
        </motion.div>
      </Link>
      
      {/* Scrollable nav items */}
      <nav className="flex-1 w-full overflow-y-auto custom-scrollbar py-2" style={{ scrollbarWidth: 'none' }}>
        <div className="flex flex-col items-center gap-6 px-1">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            const isHovered = hoveredItem === index;
            
            return (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={item.path}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 relative ${
                    isActive
                      ? 'bg-dark-400 text-white shadow-lg shadow-accent-purple/20'
                      : 'bg-dark-700 text-gray-400 hover:bg-dark-500 hover:text-white'
                  }`}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <motion.i 
                    className={`fas ${item.icon} transition-all duration-150 ${isActive || isHovered ? 'scale-110' : ''}`}
                    animate={{
                      rotate: isActive ? 360 : 0,
                      scale: isActive ? 1.2 : isHovered ? 1.1 : 1
                    }}
                  ></motion.i>
                  
                  {/* Tooltip */}
                  <motion.div 
                    className={`absolute left-full ml-4 px-3 py-1.5 bg-dark-500 rounded-lg text-xs font-medium whitespace-nowrap z-50 ${
                      isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'
                    }`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ 
                      opacity: isHovered ? 1 : 0,
                      x: isHovered ? 0 : -10
                    }}
                    style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
                  >
                    {item.label}
                    <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 transform rotate-45 w-2 h-2 bg-dark-500"></div>
                  </motion.div>
                  
                  {/* Active indicator */}
                  {isActive && (
                    <motion.div 
                      className="absolute left-0 w-1 h-8 bg-gradient-to-b from-accent-pink to-accent-purple rounded-r-md"
                      layoutId="activeIndicator"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </nav>
      
      {/* Account button at bottom - special styling */}
      <div className="mt-auto">
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link
            to="/account"
            className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 relative ${
              location.pathname === '/account'
                ? 'bg-gradient-to-br from-accent-blue to-accent-purple text-white shadow-lg shadow-accent-blue/20'
                : 'bg-dark-700 text-gray-400 hover:bg-gradient-to-br hover:from-accent-blue/80 hover:to-accent-purple/80 hover:text-white'
            }`}
            onMouseEnter={() => setHoveredItem('account')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <i className="fas fa-user-circle"></i>
            
            {/* Tooltip */}
            <motion.div 
              className={`absolute left-full ml-4 px-3 py-1.5 bg-dark-500 rounded-lg text-xs font-medium whitespace-nowrap z-50 ${
                hoveredItem === 'account' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'
              }`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ 
                opacity: hoveredItem === 'account' ? 1 : 0,
                x: hoveredItem === 'account' ? 0 : -10
              }}
              style={{ pointerEvents: hoveredItem === 'account' ? 'auto' : 'none' }}
            >
              My Account
              <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 transform rotate-45 w-2 h-2 bg-dark-500"></div>
            </motion.div>
            
            {/* Active indicator */}
            {location.pathname === '/account' && (
              <motion.div 
                className="absolute left-0 w-1 h-8 bg-gradient-to-b from-accent-blue to-accent-purple rounded-r-md"
                layoutId="activeIndicator"
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Sidebar;